import { Stack } from '@cp/ds/src/components/stack';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { ButtonProps } from '@cp/ds/src/components/button/model';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Button } from '@cp/ds/src/components/button';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { useClientProfile } from 'src/entities/client';
import { withSubscriptionClick } from 'src/features/billing';
import { withTrackClick } from 'src/shared/tracking';
import { CatalogEvents } from 'src/features/catalog';
import { withAuth } from 'src/features/auth';
import { useCatalogItem } from 'src/entities/catalog';
import { _experimentalComponentBuilder as componentBuilder } from 'src/shared/componentBuilder';
import { withConnectCatalogItemClick, withOrderNannyClick, withVacancyResponseClick } from 'src/features/catalogItem';
import { FC } from 'react';
import { ClientType } from '@common/model/client/clientType';
import { withPatronageHintsClick } from 'src/features/client/current/patronage';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { CatalogItemActionsProps } from './model';
import { Favorite, FavoriteProps } from './favorite';

export * from './model';

const ResponseButton = componentBuilder(Button)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemResponse)
  .compose(withVacancyResponseClick)
  .compose(withPatronageHintsClick)
  .compose(withSubscriptionClick)
  .compose(withAuth)
  .buildWithProps({
    color: 'pink',
  });

const ConnectButton = componentBuilder(Button)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemQuestion)
  .compose(withConnectCatalogItemClick)
  .compose(withAuth)
  .buildWithProps({
    color: 'gray',
    variant: 'secondary',
  });

const resumeButtonProps: ButtonProps = {
  color: 'gray',
  size: 'normal',
  sx: { flex: 1 },
  variant: 'primary',
};

const InviteButton = componentBuilder(Button)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemInvite)
  .compose(withOrderNannyClick)
  .compose(withSubscriptionClick)
  .compose(withAuth)
  .buildWithProps(resumeButtonProps);

const ResumeActions: FC<ButtonProps & { catalogItem: CatalogItemViewModel }> = ({ catalogItem, ...props }) => (
  <>
    <Stack direction="row" spacing={1.5} sx={{ maxWidth: '480px', width: '100%', px: { xs: '12px', md: 0 }, mt: { xs: 0, md: '20px' } }}>
      <InviteButton careerId={catalogItem.careerId} catalogItemId={catalogItem.id} clientType={ClientType.Employer}>
        Позвать
      </InviteButton>
      <ConnectButton {...resumeButtonProps} careerId={catalogItem.careerId} catalogItemId={catalogItem.id} clientType={ClientType.Employer}>
        Написать
      </ConnectButton>
    </Stack>
    {catalogItem.clientProfile && (
      <Favorite color="gray ultra light" {...(props as FavoriteProps)} authorId={catalogItem.clientProfile.id} />
    )}
  </>
);

const VacancyActions: FC<ButtonProps & { catalogItem: CatalogItemViewModel }> = ({ catalogItem, ...props }) => {
  const theme = useTheme();
  return (
    <>
      <ResponseButton {...props} careerId={catalogItem.careerId} catalogItemId={catalogItem.id} clientType={ClientType.Specialist}>
        Я могу
      </ResponseButton>
      <ConnectButton
        {...props}
        careerId={catalogItem.careerId}
        catalogItemId={catalogItem.id}
        clientType={ClientType.Specialist}
        sx={mergeSx(props.sx, { backgroundColor: theme.palette.white })}
      >
        Задать вопрос
      </ConnectButton>
    </>
  );
};

export const CatalogItemActions: FC<CatalogItemActionsProps> = ({ sx, catalogItem: originalCatalogItem }) => {
  const viewCatalogItem = useCatalogItem();
  const clientProfile = useClientProfile();
  const catalogItem = originalCatalogItem ?? viewCatalogItem;

  if (!catalogItem) {
    return null;
  }

  const isResume = catalogItem.type === CatalogItemType.Resume;
  const buttonsProps: ButtonProps = {
    sx: isResume ? {} : { flex: { xs: '1 1 50%', [MOBILE_BREAKPOINT_THRESHOLD]: 'initial' }, whiteSpace: 'nowrap' },
    disabled: clientProfile?.type ? clientTypeToSuitableCatalogItemType(clientProfile.type) !== catalogItem.type : false,
  };
  const EntityActions = isResume ? ResumeActions : VacancyActions;

  return (
    <Stack alignItems="center" direction="row" spacing={1.5} sx={sx}>
      <EntityActions catalogItem={catalogItem} {...buttonsProps} />
    </Stack>
  );
};
